// Util file for menu links

/** @typedef {import('@datahooks/types/tagging.types').OrganisationTag} OrganisationTag */
/** @typedef {import('@datahooks/types/tagging.types').ProjectTagValuesResponse} ProjectTagValuesResponse */
/** @typedef {{ id: number, title: string }} ProjectTagValueRef */

/**
 * A single active tag filter applied to the project list
 * @typedef {{ tag: number, value: number }} ActiveFilter
 * 
 * Display label for a filter along with its source filter for removal
 * @typedef {{ filter: ActiveFilter, label: string }} FilterLabel
 */

import logError from '@datahooks/metadata/ErrorLogging';
export const UNCATEGORISED_GROUP = 'Uncategorised';
export const STORAGE_KEY = 'menuLinks:prefs:v1';

/**
 * Function to derive filter labels from filters and tags
 * 
 * @param {ActiveFilter[]} filters 
 * @param {OrganisationTag[]} filterableTags 
 */
export const getFilterLabels = function(filters, filterableTags) {
    /** @type {FilterLabel[]} */
    const labels = [];

    for (const filter of filters) {
        const tag = filterableTags.find(tag => tag.tag_id === filter.tag);
        const value = tag?.tag_values.find(tag_value => tag_value.tag_value_id === filter.value);

        const label = { filter: filter, label: `${value?.title ?? 'Unknown'}` };

        if (!labels.some(l => l.label === label.label)) labels.push(label);
    }
    
    return labels;
};

/**
 * Function to filter a flat (ungrouped) list of projects based on active filters
 * 
 * @param {*} projects 
 * @param {ActiveFilter[]} filters 
 * @param {OrganisationTag[]} filterableTags 
 * @returns {any[]|null} Filtered array of projects
 */
export const filterFlatProjectList = function(projects, filters, filterableTags) {
    if (!Array.isArray(projects)) return [];

    if (filters.length === 0) return sortFlatProjects(projects);  // If no filters, return original list sorted by name

    const filteredProjects = projects.filter(project =>
      filters.every(filter => {
        const slug = filterableTags.find(t => t.tag_id === filter.tag)?.tag_slug;
        const projectTagValues = (slug && project.project_tags?.[slug]) || [];

        // Check if any tag value object has an id matching the filter value
        return projectTagValues.some(tagValue => 
          typeof tagValue === 'object' ? tagValue.id === filter.value : tagValue === filter.value
        );
      })
    );

    return sortFlatProjects(filteredProjects);
}

export const sortFlatProjects = function(projects) {
    if (!Array.isArray(projects)) return [];
    return [...projects].sort((projectA, projectB) => projectA.short_name.localeCompare(projectB.short_name), undefined, { sensitivity: 'base' });
}

/**
 * Function to filter grouped projects based on active filters.
 * Sort alphabetically within groups after filtering.
 * 
 * @param {Map<string, any[]>} projectsByGroup
 * @param {ActiveFilter[]} filters
 * @param {OrganisationTag[]} filterableTags
 * 
 * @returns {Map<string, any[]>} Filtered map of group name to array of projects
 * 
 */
export const filterGroupedProjects = function(projectsByGroup, filters, filterableTags) {

    if (!projectsByGroup || !(projectsByGroup instanceof Map)) return new Map();    // if no grouped projects, return empty map

    // Helper to check if project matches all filters
    const projectMatchesAllFilters = (project, filters, filterableTags) => {
      return filters.every(filter => {
        const tagSlug = filterableTags.find(t => t.tag_id === filter.tag)?.tag_slug;
        const projectTagValues = project.project_tags?.[tagSlug] || [];
        return projectTagValues.some(tagValue => 
          typeof tagValue === 'object' ? tagValue.id === filter.value : tagValue === filter.value
        );
      });
    };

    // If no filters, return a copy of the original groups (restoring all projects)
    if (filters.length === 0) {
      const copy = new Map();
      projectsByGroup.forEach((projectList, groupKey) => {
        copy.set(groupKey, Array.isArray(projectList) ? [...projectList] : []);
      });
      return sortGroupedProjects(copy);
    }

    // Build a new filtered map instead of mutating the input, to ensure we don't lose projects when toggling filters
    const filteredMap = new Map();
    projectsByGroup.forEach((projectList, groupKey) => {
      const filteredProjects = projectList.filter(project => 
        projectMatchesAllFilters(project, filters, filterableTags)
      );
      if (filteredProjects.length > 0) {
        filteredMap.set(groupKey, filteredProjects);
      }
    });

    return sortGroupedProjects(filteredMap);
};

/**
 * Sort projects within each group by name alphabetically
 * 
 * @param {Map<string>, any[]} projectsByGroup
 * 
 * @returns {Map<string>, any[]} Sorted map of group name to array of projects 
 */
export const sortGroupedProjects = function(projectsByGroup) {

    if (!projectsByGroup || !(projectsByGroup instanceof Map)) {
      return new Map();
    }

    const sorted = new Map();
    projectsByGroup.forEach((list, key) => {
      const copy = Array.isArray(list) ? [...list].sort((projectA, projectB) => projectA.short_name.localeCompare(projectB.short_name, undefined, { sensitivity: 'base' })) : [];
      sorted.set(key, copy);
    });

    return sorted;
}

/**
 * Function to group projects by a selected tag
 * 
 * @param {*} projects
 * @param {OrganisationTag|null} selectedGroupTag - Null until a groupable tag is chosen; returns an empty Map.
 * @param {OrganisationTag[]} groupableTags
 *
 * @return {Map<string, any[]>} Map of group name to array of projects
**/
export const buildGroupedProjects = function(projects, selectedGroupTag, groupableTags) {
  if (projects?.length < 1 || selectedGroupTag === undefined || groupableTags === undefined) {
    return new Map();
  }

    if (!selectedGroupTag || !groupableTags.some(t => t.tag_slug === selectedGroupTag.tag_slug)) {
      return new Map();
    }

    // Map to store grouped projects
    const projectsByGroup = new Map();

    selectedGroupTag.tag_values.forEach(tag_value => {
        projectsByGroup.set(tag_value.title, []);  // Initialize each group with an empty array to preserve order (by tag_value.order_by as per hook data)
    });
    projectsByGroup.set(UNCATEGORISED_GROUP, []);  // Add 'Uncategorised' group for projects with no relevant tag value

    (projects || []).forEach((project) => {
      const ids = project.project_tags?.[selectedGroupTag.tag_slug] ?? []; // Get tag values for the current project and selected tag 
      const hasTagForSelectedGroup = ids.some(id => selectedGroupTag.tag_values.some(tag_value => tag_value.tag_value_id === id.id)); // Check if any of the projects tag values match the selected tag's values

      if (ids.length === 0 || !hasTagForSelectedGroup) {  // We have no relevant tag values for this project

        if (!projectsByGroup.has(UNCATEGORISED_GROUP)) projectsByGroup.set(UNCATEGORISED_GROUP, []);  // Add the 'Uncategorised' group if not present

        projectsByGroup.get(UNCATEGORISED_GROUP).push(project); // Push this project into the 'Uncategorised' group because it has no relevant tag value

        return; // move to next project
      }

      // Project has a relevant tag value, add it to the appropriate group
      for (const id of ids) {   // Loop through the tag values for this project for the selected group tag
        const tagValue = selectedGroupTag.tag_values.find(tag_value => tag_value.tag_value_id === id.id);  // Find the tag value details

        if (!projectsByGroup.has(tagValue.title)) { // If we don't have a group for this tag value yet
          projectsByGroup.set(tagValue.title, []);  // add it
        }

        projectsByGroup.get(tagValue.title).push(project); // Push this project into the group
      }
    });

    //Remove any empty groups
    for (const [groupKey, projectList] of projectsByGroup.entries()) {

      if (projectList.length === 0) {
        projectsByGroup.delete(groupKey);
      }
    }

    return projectsByGroup;
}

/**
 * Build an index of project tag assignments with enriched tag titles:
 *   Map<project_id, Record<tag_slug, Array<{id: number, title: string}>>>
 *
 * @param {ProjectTagValuesResponse} rows
 * @param {OrganisationTag[]} orgTags
 * @returns {Map<number, Record<string, Array<{id: number, title: string}>>>}
 */
export const buildProjectTagIndex = function(rows, orgTags = []) {
  const map = new Map();

  if (!Array.isArray(rows)) return map;

  for (const row of rows) {
    const current = map.get(row.project_id) ?? {};
    
    // Enrich tag value IDs with titles from orgTags
    const tagValueIds = Array.isArray(row.tag_value_ids) ? row.tag_value_ids : [];
    const orgTag = orgTags.find(tag => tag.tag_slug === row.tag_slug);

    if (!orgTag && tagValueIds.length > 0) {
      logError(`buildProjectTagIndex: No matching orgTag found for slug "${row.tag_slug}"`, null, {
        availableSlugs: orgTags.map(t => t.tag_slug),
        searchedSlug: row.tag_slug
      }, false);
    }

    current[row.tag_slug] = tagValueIds.map(id => {
      const tagValue = orgTag?.tag_values.find(tv => tv.tag_value_id === id);

      if (!tagValue && orgTag) {
        logError(`buildProjectTagIndex: No matching tag value for id ${id} in slug "${row.tag_slug}"`, null, {
          availableIds: orgTag.tag_values.map(tv => tv.tag_value_id),
          searchedId: id
        }, false);
      }

      return {
        id,
        title: tagValue?.title ?? `Unknown (${id})`
      };
    });

    map.set(row.project_id, current);
  }
  return map;
};

/**
 * Attach project_tags to each project using the index.
 *
 * @param {any[]} projects
 * @param {Map<number, Record<string, ProjectTagValueRef[]>>} index
 * 
 * @returns {any[]}
 */
export const applyProjectTags = function(projects, index) {

  if (!Array.isArray(projects)) return [];

  return projects.map(p => ({
    ...p,
    project_tags: index.get(p.projectId) ?? {},
  }));
};